import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  InputAdornment,
} from '@mui/material'
import { ListPagination } from '../components/ListPagination'
import { Search, FilterList, LocationOn } from '@mui/icons-material'
import { US_STATES, DIVISIONS, DIVISION_LABELS, formatCityState } from '@tnf-recruit/shared'
import { useSchoolSearch } from '../hooks/useSchoolSearch'
import { SchoolLogo } from '../components/SchoolLogo'
import { AppPageHeader } from '../components/AppPageHeader'
import { LoadingStatePanel, EmptyStatePanel } from '../components/StatePanel'

const SCHOOLS_PER_PAGE = 20

export const SchoolBrowsePage = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [state, setState] = useState('')
  const [division, setDivision] = useState('')
  const [page, setPage] = useState(1)
  const navigate = useNavigate()

  const searchParams = {
    query: searchQuery || undefined,
    state: state || undefined,
    division: division || undefined,
    page,
    limit: SCHOOLS_PER_PAGE,
    sortBy: 'name' as const,
    sortOrder: 'asc' as const,
  }

  const { data: searchResult, isLoading } = useSchoolSearch(searchParams)
  const schools = searchResult?.data ?? []
  const pagination = searchResult?.pagination

  const handleResetFilters = () => {
    setSearchQuery('')
    setState('')
    setDivision('')
    setPage(1)
  }

  return (
    <Box>
      <AppPageHeader
        title="Browse Schools"
        description="Explore colleges and universities and find programs that fit your goals."
      />

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search by school or city"
                value={searchQuery}
                onChange={e => {
                  setSearchQuery(e.target.value)
                  setPage(1)
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>State</InputLabel>
                <Select
                  value={state}
                  label="State"
                  onChange={e => {
                    setState(e.target.value)
                    setPage(1)
                  }}
                >
                  <MenuItem value="">All States</MenuItem>
                  {US_STATES.map(s => (
                    <MenuItem key={s.code} value={s.code}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Division</InputLabel>
                <Select
                  value={division}
                  label="Division"
                  onChange={e => {
                    setDivision(e.target.value)
                    setPage(1)
                  }}
                >
                  <MenuItem value="">All Divisions</MenuItem>
                  {DIVISIONS.map(div => (
                    <MenuItem key={div} value={div}>
                      {DIVISION_LABELS[div]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                startIcon={<FilterList />}
                fullWidth
                sx={{ height: '56px' }}
                onClick={() => setPage(1)}
              >
                Apply
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                fullWidth
                sx={{ height: '56px' }}
                onClick={handleResetFilters}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Loading */}
      {isLoading && (
        <LoadingStatePanel
          title="Loading schools"
          description="Refreshing school search results."
        />
      )}

      {/* Results */}
      {!isLoading && (
        <Grid container spacing={3}>
          {schools.map(school => (
            <Grid item xs={12} md={6} lg={4} key={school.id}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <SchoolLogo name={school.name} logoUrl={school.logoUrl} sx={{ mr: 2 }} />
                    <Box>
                      <Typography variant="h6">{school.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatCityState(school.city, school.state)}
                      </Typography>
                    </Box>
                  </Box>

                  <Box mb={2} display="flex" gap={1} flexWrap="wrap">
                    <Chip label={DIVISION_LABELS[school.division]} size="small" color="primary" />
                    {school.conference && (
                      <Chip label={school.conference} size="small" variant="outlined" />
                    )}
                  </Box>

                  <Box display="flex" alignItems="center" mb={2}>
                    <LocationOn sx={{ fontSize: 18, mr: 0.5, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {formatCityState(school.city, school.state)}
                    </Typography>
                  </Box>

                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => navigate(`/schools/${school.id}`)}
                    >
                      View Details
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}

          {schools.length === 0 && !isLoading && (
            <Grid item xs={12}>
              <EmptyStatePanel
                title="No schools found"
                description="Try adjusting your search text, state, or division filters."
              />
            </Grid>
          )}
        </Grid>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={4}>
          <ListPagination count={pagination.totalPages} page={page} onChange={setPage} />
        </Box>
      )}
    </Box>
  )
}
